import Model from "../../../../model/Model";
import React, {useEffect, useState} from "react";
import Asset, {AssetCategory} from "../../../../model/Asset";
import AssetConversion from "../../../../model/AssetConversion";
import Liability from "../../../../model/Liability";
import Snapshot from "../../../../model/Snapshot";
import {INetWorthCalc} from "../../../../components/calculator/Calculator";
import {useResources} from "../../../../stores/ResourceProvider";
import NetWorthReport from "./NetWorthReport";
import PrintReport from "../../../../components/page/PrintReport";
import {dateToLocalFormat} from "../../../../stores/StoreUtilities";
import {Box, FormLabel, Typography, useTheme} from "@mui/material";

const NetWorthPrintReport = ({
  model,
  netWorthCalc
} : {
  model: Model
  netWorthCalc?: INetWorthCalc
}) => {
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [liquidAssets, setLiquidAssets] = useState<Asset[]>([])
  const [assetConversions, setAssetConversions] = useState<AssetConversion[]>([])
  const [realAssets, setRealAssets] = useState<Asset[]>([])
  const [personalAssets, setPersonalAssets] = useState<Asset[]>([])
  const [otherAssets, setOtherAssets] = useState<Asset[]>([])
  const [liabilities, setLiabilities] = useState<Liability[]>([])
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | undefined>()
  const [currentNetWorthCalc, setCurrentNetWorthCalc] = useState<INetWorthCalc | undefined>()

  const {calculator, modelStore} = useResources()

  const theme = useTheme()
  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
  }

  useEffect(() => {
    if (model) {
      setCurrentModel(model)
      const liquid = model.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.LiquidInvestableAssets)
      setLiquidAssets(liquid)
      setAssetConversions(model.assetConversions)
      const real = model.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.RealEstateAndProperty)
      setRealAssets(real)
      const personal = model.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.PersonalProperty)
      setPersonalAssets(personal)
      const other = model.assets.filter((asset: Asset) => asset.assetCategory === AssetCategory.OtherAssets)
      setOtherAssets(other)
      setLiabilities(model.liabilities)
      setSelectedSnapshot(model.snapshots[0])
      if (netWorthCalc) {
        setCurrentNetWorthCalc(netWorthCalc)
      } else {
        setCurrentNetWorthCalc(calculator.calculateNetWorth(liquid, real, personal, other, model.liabilities, model.snapshots[0]))
      }
    }
  }, [model])

  const getReportDetails = () => {
    const user = modelStore.currentUser
    if (user && currentModel && selectedSnapshot) {
      const fullDate = dateToLocalFormat(selectedSnapshot.date, "MMM d, yyyy")
      return (
        <React.Fragment>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Date:</FormLabel></Box>
            <Box><Typography variant="body2">{fullDate}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Name:</FormLabel></Box>
            <Box><Typography variant="body2">{user!.fullName}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Model:</FormLabel></Box>
            <Box><Typography variant="body2">{currentModel!.name} ({currentModel!.appliedPlan?.name})</Typography></Box>
          </Box>
        </React.Fragment>
      )
    } else {
      return null
    }
  }


  if (!selectedSnapshot || !currentNetWorthCalc) {
    return null
  }

  return (
    <PrintReport isPrinting={true}
                 title="Net Worth Report"
                 details={getReportDetails()}
    >
      <NetWorthReport user={modelStore.currentUser!}
                      model={model}
                      liquidAssets={liquidAssets}
                      assetConversions={assetConversions}
                      realAssets={realAssets}
                      personalAssets={personalAssets}
                      otherAssets={otherAssets}
                      liabilities={liabilities}
                      snapshot={selectedSnapshot}
                      networthCalc={currentNetWorthCalc}
      />
    </PrintReport>
  )

}

export default NetWorthPrintReport